import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ApiError, getMessageFromError } from '@pro4-erp/core-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useTranslation } from 'react-i18next';
import { getVisitHistory } from '../api/getVisitHistory';
import { getVisitLinkNextCloud } from '../api/getVisitLinkNextCloud';
import { NextCloudLink } from '../types';

const HistoryItem = ({ item, woUid }: { item: string; woUid: string }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync } = useMutation<NextCloudLink, ApiError>({
    mutationFn: () => getVisitLinkNextCloud(item, woUid),
    mutationKey: ['history-link-id', item],
    onSuccess: (data) => {
      if (!data?.status) {
        return;
      }
      window.open(data.url, '_blank');
    },
    onError: (error: Error) => {
      console.log(error);
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
    cacheTime: 1000 * 5 * 60,
  });
  const getHistoryLink = async () => {
    const mutation = queryClient.getMutationCache().find<NextCloudLink>({
      mutationKey: ['history-link-id', item],
    });
    const link = mutation?.state?.data?.status && mutation?.state?.data.url;
    if (!link) {
      mutateAsync();
      return;
    }
    window.open(link, '_blank');
  };

  return (
    <ListItem
      sx={{ maxWidth: '400px' }}
      secondaryAction={
        <Button
          variant="contained"
          onClick={() => {
            getHistoryLink();
          }}
        >
          {t('Visit documents', { defaultValue: 'Visit documents' })}
        </Button>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <VerifiedUserIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText primary={item} />
    </ListItem>
  );
};

const VisitHistory = ({ visitId, woUid }: { visitId: string; woUid: string }) => {
  const query = useQuery<string[], ApiError>(
    ['visit-history-id', visitId],
    () => getVisitHistory(visitId),
    { refetchOnWindowFocus: false, staleTime: 1000 * 5 * 60 }
  );

  if (query.isFetching) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box>
      <List sx={{ mx: '4rem' }}>
        {query.error && <ListItemText primary="ALARM ERROR" />}
        {query.isFetched &&
          query.data &&
          query.data.map((item) => {
            return <HistoryItem key={item} item={item} woUid={woUid} />;
          })}
      </List>
    </Box>
  );
};

export default VisitHistory;
